import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Grid, IconButton, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { getUsers, handleToggleAddNewUserDialog, handleToggleChangeRoleDialog, handleToggleEnableUserDialog } from '../../store/usermanagement-slice/usermanagement-slice';
import Loader from '../common/Loader';
import { Header } from '../common/Header';
import { ChangeRoleDialog } from './change-role-dialog';
import { EnableUserDialog } from './enable-user-dialog';
import { AddNewUserDialog } from './add-new-user-dialog';
import moment from 'moment';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { axiosInstance } from '../../axios/axiosHandler';
import { EndPoints } from '../../axios/endPoints';
import { authHeader } from '../../store/auth-slice/auth-slice';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import signalRService from '../../signalr/config';

export interface User {
  userID: number,
  username: string,
  email: string,
  role: string,
  isEnabled: boolean,
  isConfirmed: boolean,
  isCurrentUser: boolean
}

interface UserSubscription {
  id?: string;
  subscriptionName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  subscriptionStatus?: string | null;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    border: '1px solid rgba(0, 0, 0, 0.87)',
    fontSize: 11,
    borderRadius: 0,
  },
}));

const InfoRow = ({ label, value }: any) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    <Typography component="span" color="text.secondary" variant="body2" fontWeight={600}>
      {label}:
    </Typography>

    <Typography component="span"
      noWrap
      sx={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
      }}
      variant="body2">
      <LightTooltip title={value}>
        <span>{value}</span>
      </LightTooltip>
    </Typography>

  </Box>
);


const columns = (dispatch: any, t: Function, expandedRows: any, toggleRow: any): GridColDef[] => [
  { field: 'userID', headerName: t('USERMANAGEMENT_ID_COLUMN'), width: 100 },
  {
    field: 'email', headerName: t('USERMANAGEMENT_EMAIL_COLUMN'), width: 400, renderCell: (params) => {
      const isExpanded = expandedRows.has(params.id as number);

      return (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => toggleRow(params.id as number)}
          >
            <Typography variant="body2" noWrap={!isExpanded}
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}>
              <LightTooltip title={params.value}>
                <span>{params.value}</span>
              </LightTooltip>

            </Typography>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>

          {isExpanded && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <InfoRow label={t('USERMANAGEMENT_FIRST_NAME_COLUMN')} value={params.row.firstName} />
              <InfoRow label={t('USERMANAGEMENT_LAST_NAME_COLUMN')} value={params.row.lastName} />
              <InfoRow label={t('USERMANAGEMENT_COMPANY_COLUMN')} value={params.row.companyName} />
              <InfoRow label={t('USERMANAGEMENT_JOB_TITLE_COLUMN')} value={params.row.jobTitle} />
              <InfoRow label={t('USERMANAGEMENT_PHONE_NUMBER_COLUMN')} value={params.row.phoneNumber} />
            </Box>
          )}
        </Box>
      );
    }
  },
  { field: 'selfRegistrated', headerName: t('USERMANAGEMENT_SELF_REGISTRATED_COLUMN'), width: 200 },
  { field: 'role', headerName: t('USERMANAGEMENT_ROLE_COLUMN'), width: 200 },
  { field: 'isEnabled', headerName: t('USERMANAGEMENT_ENABLED_COLUMN'), width: 200, renderCell: ({ value }) => { return value ? t('YES_LABEL') : t('NO_LABEL') } },
  { field: 'isConfirmed', headerName: t('USERMANAGEMENT_CONFIRMED_COLUMN'), width: 200, renderCell: ({ value }) => { return value ? t('YES_LABEL') : t('NO_LABEL') } },
  { field: 'subscriptionStatus', renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_CURRENT_SUBSCRIPTION_STATUS')), width: 300, renderCell: ({ value }) => { return t(value) } },
  {
    field: '', headerName: t('USERMANAGEMENT_ACTION_COLUMN'), minWidth: 200, flex: 1, renderCell: (params) => {
      return <Grid container display="flex" justifyContent="center" spacing={4}>
        <Grid item>
          <Tooltip title={params.row.isEnabled ? t('USER_MANAGEMENT_DISABLE_USER') : t('USER_MANAGEMENT_ENABLE_USER')}>
            <IconButton aria-label="enable" onClick={() => dispatch(handleToggleEnableUserDialog({
              open: true, body: {
                email: params.row.email,
                enable: !params.row.isEnabled
              }
            }))}>
              {params.row.isEnabled ? <ToggleOffIcon className='info-icon' /> : <ToggleOnIcon className='info-icon' />}
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title={t('USER_MANAGEMENT_CHANGE_ROLE')}>
            <IconButton aria-label="change" onClick={() => {
              dispatch(handleToggleChangeRoleDialog({
                open: true, body: {
                  email: params.row.email
                }
              }))
            }}><ManageAccountsIcon className='download-icon' /></IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    }
  }
];

const subscriptionColumns = (dispatch: any, t: Function): GridColDef[] => [
  { field: 'subscriptionName', width: 300, headerName: (t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_NAME_COLUMN')), renderCell: ({ value }) => { return value ? t(value) : '-' } },
  { field: 'startDate', width: 250, headerName: (t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_START_DATE_COLUMN')), valueFormatter: (params) => { if (!params.value) return ''; return moment.utc(params.value).local().format('DD/MM/YYYY') } },
  { field: 'endDate', width: 250, headerName: (t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_END_DATE_COLUMN')), valueFormatter: (params) => { if (!params.value) return ''; return moment.utc(params.value).local().format('DD/MM/YYYY') } },
  {
    field: 'subscriptionStatus', minWidth: 250, flex: 1, headerName: (t('USERMANAGEMENT_IS_ACTIVE_SUBSCRIPTION_COLUMN')), renderCell: (params) => {
      const status = params.value;
      if (!status) {
        return '-';
      }

      const isActiveLikeStatus =
        status === "BE_MEDML_SUBSCRIPTION_STATUS_ACTIVE" ||
        status === "BE_MEDML_SUBSCRIPTION_TRIAL_NAME";

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {t(status)}
          <FiberManualRecordIcon
            color={isActiveLikeStatus ? 'success' : 'error'}
            fontSize="small"
          />
        </Box>
      )
    }
  },

]

export const renderHeaderTwoLines = (translated: string) => {
  const words = translated.split(' ');

  const firstLine = words[0];
  const secondLine = words.slice(1).join(' ');

  return (
    <div style={{ lineHeight: 1.2 }}>
      {firstLine}
      {secondLine && <><br />{secondLine}</>}
    </div>
  );
};


export function UserManagement() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { loadingUsers, users, reloadUsers, openLoader } = useAppSelector((state: RootState) => state.usermanagement);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [showSubscriptionsTable, setShowSubscriptionsTable] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");

  const mapUserSubscriptionToRows = (payload: UserSubscription | UserSubscription[] | null | undefined): UserSubscription[] => {
    if (!payload) {
      return [];
    }

    return Array.isArray(payload) ? payload : [payload];
  };

  function handleAddNewUserDialogToggle() {
    dispatch(handleToggleAddNewUserDialog(true));
  }

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const expandedRows = new Set(prev);
      expandedRows.has(id) ? expandedRows.delete(id) : expandedRows.add(id);
      return expandedRows;
    });
  };

  const loadUserSubscriptions = useCallback(async (username: string) => {
    try {
      const response = await axiosInstance.get<UserSubscription | UserSubscription[] | null>(
        EndPoints.USER_MANAGEMENT_GET_USER_SUBSCRIPTION + `?username=${encodeURIComponent(username)}`,
        authHeader()
      );

      setUserSubscriptions(mapUserSubscriptionToRows(response.data));
      return true;
    }
    catch (error) {
      console.error('Error fetching user subscription', error);
      setUserSubscriptions([]);
      return false;
    }
  }, []);

  const openSubscriptionsTable = async (username: string) => {
    if (selectedUser === username && showSubscriptionsTable) {
      setShowSubscriptionsTable(false);
      return;
    }

    const loaded = await loadUserSubscriptions(username);
    setSelectedUser(username);
    setShowSubscriptionsTable(loaded);
  }


  useEffect(() => {
    dispatch(getUsers());
    setShowSubscriptionsTable(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUsers]);

  useEffect(() => {
    const unsubscribe = signalRService.subscribeToSubscriptionUpdates(() => {
      void dispatch(getUsers());

      if (showSubscriptionsTable && selectedUser) {
        void loadUserSubscriptions(selectedUser);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch, loadUserSubscriptions, selectedUser, showSubscriptionsTable]);

  if (loadingUsers) return <Loader open={loadingUsers} />;

  if (users.length === 0)
    return (
      <Card style={{ marginTop: '5px' }}>
        <Header title={t('USER_MANAGEMENT_NO_USERS')} />
        <Button
          onClick={handleAddNewUserDialogToggle}
          variant="contained"
          startIcon={<AddIcon />}
        >
          {t('USER_MANAGEMENT_ADD_NEW_USER')}
        </Button>
        <AddNewUserDialog />
        <ChangeRoleDialog />
        <EnableUserDialog />
      </Card>
    );

  return (
    <>
      <Card style={{ marginTop: '5px' }}>
        {openLoader ? <Loader open={openLoader} /> : ''}
        <Header title={t('USER_MANAGEMENT_USERS_LABEL')} />
        <Grid p={1.5} display="flex" justifyContent="flex-end" alignItems="flex-end">
          <Button size="small" onClick={() => {
            handleAddNewUserDialogToggle();
          }} className='button-color' variant="contained" startIcon={<AddIcon />}>{t('USER_MANAGEMENT_ADD_NEW_USER')}</Button>
        </Grid>
        <Box sx={{ overflow: "auto" }} p={1.5}>
          <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
            <DataGrid
              autoHeight
              rows={users}
              columns={columns(dispatch, t, expandedRows, toggleRow)}
              sx={{
                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', },
                '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
                  borderRight: '1px solid #E0E0E0',
                },
                '& .MuiDataGrid-cell:last-child, & .MuiDataGrid-columnHeader:last-child': {
                  borderRight: 'none',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: paginationModel,
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              getRowHeight={(params) =>
                expandedRows.has(params.id as number) ? 150 : null
              }
              getRowId={(row) => row.userID}
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              componentsProps={{
                pagination: {
                  labelRowsPerPage: t('ROWS_PER_PAGE_TEXT')
                }
              }}
              rowHeight={35}
              onPaginationModelChange={(newModel) => {
                setPaginationModel(newModel);
                setShowSubscriptionsTable(false);
              }}
              columnHeaderHeight={40}
              onRowClick={(params) => openSubscriptionsTable(params.row.username)}
              slotProps={{
                row: {
                  title: t('USERMANAGEMENT_ROW_CLICK_TOOLTIP'),
                },
              }}
            />
          </Box>
        </Box>

        <ChangeRoleDialog />
        <EnableUserDialog />
        <AddNewUserDialog />
      </Card>

      {showSubscriptionsTable ?

        <Card style={{ marginTop: '15px' }}>
          <Header
            title={t('PROFILE_SUBSCRIPTION_DETAILS')}
          />

          {userSubscriptions.length > 0 ?

            <Box sx={{ overflow: "auto" }} p={1.5}>
              <Typography variant="subtitle1" sx={{ mb: 2, mt: 2 }}>
                {t('USERMANAGEMENT_SUBSCRIPTION_TABLE_SUBTITLE')} <strong>{selectedUser}</strong>
              </Typography>
              <Box sx={{ width: '100%', display: "table", tableLayout: "fixed" }}>
                <DataGrid
                  autoHeight
                  columns={subscriptionColumns(dispatch, t)}
                  rows={userSubscriptions}
                  getRowId={(row) => row.id ?? `${row.subscriptionName ?? 'subscription'}-${row.startDate ?? ''}-${row.endDate ?? ''}`}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', },
                    '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #E0E0E0',
                    },
                    '& .MuiDataGrid-cell:last-child, & .MuiDataGrid-columnHeader:last-child': {
                      borderRight: 'none',
                    }
                  }}
                />
              </Box>
            </Box>
            :
            <Box sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
              bgcolor: 'background.paper',
              flexDirection: 'column'
            }}
            >
              <Typography color="text.secondary">
                {t('USERMANAGEMENT_NO_SUBSCRIPTIONS_MESSAGE')} <strong>{selectedUser}</strong>
              </Typography>
            </Box>
          }
        </Card>


        : null
      }
    </>

  );
}

export default UserManagement;
